// Canvas 2D 上で付箋本文の文字サイズ・幅を求める計測ユーティリティ。
//
// PDF 出力（canvas-pdf-export.ts）が持っていた実装をそのまま移し、サムネイル（canvas-thumbnail.ts）
// からも同じ best-fit を使えるようにする。サムネイルの帯は「実表示と同じ行割り」であることが要件で、
// 文字サイズが違えば折り返し位置も変わるため、推定ではなく本番と同一の計測を共有する。

import type { TableColumnWidthsList } from "@refinement-canvas/domain";
import { layoutMarkdownTextForCanvas } from "./markdown-canvas-layout";
import {
  STICKY_FONT_MAX_PX,
  STICKY_FONT_MIN_PX,
  computeBestFitFontSize,
} from "./sticky-font-fit";

/** CanvasTextAlign（start / end を含む）をレイアウトの 3 値アンカーへ丸める。 */
export function resolveLayoutAlign(align: CanvasTextAlign): layoutMarkdownTextForCanvas.Anchor {
  return align === "center" || align === "right" ? align : "left";
}

/** レイアウト計算へ注入する幅計測関数（ctx.measureText を包む）。 */
export function createCanvasMeasure(
  ctx: CanvasRenderingContext2D
): layoutMarkdownTextForCanvas.Measure {
  return (text, fontPx, font) => {
    ctx.font = canvasFontFor(fontPx, font);
    return ctx.measureText(text).width;
  };
}

/**
 * Markdown のインラインコードに使う等幅フォントの並び。
 * DOM 側の `--font-code`（styles.css）と同じ並びにし、画面と PDF・サムネイルの文字幅をそろえる。
 */
export const CANVAS_MONOSPACE_FONT_FAMILY =
  '"Noto Sans Mono", "Roboto Mono", "SF Mono", Consolas, "BIZ UDGothic", monospace';

/**
 * Canvas 2D の `ctx.font` に設定する CSS font 指定を組み立てる。
 * CSS の font ショートハンドは `font-style font-weight font-size font-family` の順である必要がある。
 * 装飾を含まない本文（`{bold, italic:false, monospace:false}`）では従来と同一の文字列を返す。
 */
export function canvasFontFor(
  fontPx: number,
  font: layoutMarkdownTextForCanvas.MeasureFont
): string {
  const family = font.monospace ? CANVAS_MONOSPACE_FONT_FAMILY : "sans-serif";
  return `${font.italic ? "italic " : ""}${font.bold ? "bold " : ""}${fontPx}px ${family}`;
}

/**
 * PDF 出力時の付箋本文に使う文字サイズ（スケール後 px）を、利用可能領域へちょうど収まるよう求める。
 * 画面表示（sticky-font-fit.ts）と同じ二分探索アルゴリズムを共有し、Markdown 整形後のレイアウト
 * （見出しの倍率・箇条書きインデントを含む）で高さ・幅を判定する。
 * 戻り値はスケール後の px（drawMarkdownText の baseFontPx へそのまま渡せる）。
 */
export function computeStickyExportFontPx(
  ctx: CanvasRenderingContext2D,
  text: string,
  availWidth: number,
  availHeight: number,
  scaleY: number,
  align: CanvasTextAlign,
  /**
   * 本文中の表ごとの保存済み列幅（ブロック順）。渡さないと自動列幅で収まりを判定してしまい、
   * 実際に描かれる列幅・折り返しと食い違った文字サイズになる。
   */
  tableColumnWidths?: TableColumnWidthsList | undefined
): number {
  if (availWidth <= 0 || availHeight <= 0) return STICKY_FONT_MIN_PX * scaleY;
  const measure = createCanvasMeasure(ctx);
  const layoutAlign = resolveLayoutAlign(align);
  const fits = (fontPx: number): boolean => {
    const layout = layoutMarkdownTextForCanvas({
      align: layoutAlign,
      baseFontPx: fontPx * scaleY,
      maxWidth: availWidth,
      measure,
      // 表の外枠線（画面 1px）は文字と同じ倍率で換算し、描画（drawMarkdownText / drawTextSkeleton）と
      // 同じ列幅でフィットを判定する。
      pxScale: scaleY,
      text,
      ...(tableColumnWidths === undefined ? {} : { tableColumnWidths }),
    });
    // 高さが収まり、かつ折り返し後の幅も超過しないことを確認する
    // （overflowsWidth は 1 文字も収まらない幅超過を含む。画面側 measureStickyFitFontSize の
    //  scrollWidth 判定と同等）。
    return layout.totalHeight <= availHeight && !layout.overflowsWidth;
  };
  const unscaled = computeBestFitFontSize({
    minPx: STICKY_FONT_MIN_PX,
    maxPx: STICKY_FONT_MAX_PX,
    fits,
  });
  return unscaled * scaleY;
}
